import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TemplateEmailPreview } from '../../../model/TemplateEmailPreview';
import { Language } from '../../../model/Language';
import { EmailDisplay } from '../TemplatePreview/EmailDisplay';
import { getLanguageFlag } from '../../../util/TranslationHelpers';

interface Props {
    open: boolean;
    onClose: () => void;
    templatePreview: TemplateEmailPreview | null;
    templateId: string | null;
    customerId: string | null;
    loading: boolean;
}

const TemplateEmailPreviewDialog: React.FC<Props> = (props) => {
    const { open, onClose, templatePreview, templateId, customerId, loading } = props;
    const { t } = useTranslation();

    // Extract available languages from previews object keys
    const availableLanguages: Language[] = templatePreview
        ? Object.keys(templatePreview.previews)
              .filter(key => key === 'en' || key === 'de')
              .map(key => (key === 'en' ? Language.EN : Language.DE))
        : [];

    // Initialize selectedLanguage to first available language
    const [selectedLanguage, setSelectedLanguage] = React.useState<Language | null>(
        availableLanguages.length > 0 ? availableLanguages[0] : null
    );

    // Update selectedLanguage when templatePreview changes
    React.useEffect(() => {
        if (availableLanguages.length > 0 && !selectedLanguage) {
            setSelectedLanguage(availableLanguages[0]);
        }
    }, [availableLanguages, selectedLanguage]);

    const handleLanguageChange = (event: { target: { value: Language } }) => {
        setSelectedLanguage(event.target.value);
    };

    // Convert Language enum to string key for accessing previews
    const languageKey = selectedLanguage === Language.DE ? 'de' : selectedLanguage === Language.EN ? 'en' : null;
    const selectedPreview = languageKey && templatePreview ? templatePreview.previews[languageKey] : null;

    const handleOpenLearningPage = () => {
        if (!templateId || !customerId) {
            return;
        }
        const previewUrl = `/customers/${customerId}/templates/${templateId}/preview`;
        window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        {selectedPreview?.templateName || t('templateEmailPreview.title', 'Template Preview')}
                    </Box>
                    {availableLanguages.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={selectedLanguage || ''}
                                onChange={handleLanguageChange as any}
                                displayEmpty
                                renderValue={(value) => {
                                    if (!value) return '';
                                    const flag = getLanguageFlag(value as Language);
                                    return `${flag} ${(value as Language).toUpperCase()}`;
                                }}
                            >
                                {availableLanguages.map(language => (
                                    <MenuItem key={language} value={language}>
                                        {getLanguageFlag(language)} {language.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : selectedPreview ? (
                    <EmailDisplay
                        filledSenderAddress={selectedPreview.senderAddress}
                        filledSubject={selectedPreview.subject}
                        filledBody={selectedPreview.body}
                        onClose={onClose}
                    />
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="look2xBlue">
                    {t('common.close', 'Close')}
                </Button>
                <Button
                    variant="contained"
                    color="look2xBlue"
                    onClick={handleOpenLearningPage}
                    disabled={!templateId || !customerId || loading}
                >
                    {t('demoMail.toLearningPage', 'Zur Lernseite')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplateEmailPreviewDialog;

